import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../../api";
import type { Company, Contact } from "../../models";
import type { AuthUser } from "../../api/common";
import type { CompanyContextValue } from "./CompanyContext.types";

export function useCompanyContextViewModel(user: AuthUser | undefined): CompanyContextValue {
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
	const [contact, setContact] = useState<Contact | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const setSelectedCompany = useCallback(
		(company: Company | null) => {
			setSelectedCompanyState(company);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (company) {
						next.set("company", company.id);
					} else {
						next.delete("company");
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!user?.id) {
			return;
		}

		let cancelled = false;

		async function fetchData() {
			setIsLoading(true);
			try {
				const fetchedContact = await API.Data.Contacts.get.byId(user!.id);
				if (cancelled) return;
				setContact(fetchedContact);

				const fetchedCompanies = await API.Data.Companies.get.byContact(fetchedContact);
				if (cancelled) return;

				fetchedCompanies.sort((a, b) => {
					if (a.name === "Rogo AG" || a.name === "ROGO Software") return -1;
					if (b.name === "Rogo AG" || b.name === "ROGO Software") return 1;
					return a.name.localeCompare(b.name);
				});

				setCompanies(fetchedCompanies);

				// Resolve initial selection from URL or auto-select
				const urlCompanyId = searchParams.get("company");
				if (urlCompanyId) {
					const match = fetchedCompanies.find((c) => c.id === urlCompanyId);
					if (match) {
						setSelectedCompanyState(match);
					} else {
						// Invalid URL param, fall back to first
						const first = fetchedCompanies[0] ?? null;
						setSelectedCompanyState(first);
						if (first) {
							setSearchParams(
								(prev) => {
									const next = new URLSearchParams(prev);
									next.set("company", first.id);
									return next;
								},
								{ replace: true },
							);
						}
					}
				} else if (fetchedCompanies.length >= 1) {
					const first = fetchedCompanies[0];
					setSelectedCompanyState(first);
					if (fetchedCompanies.length > 1) {
						setSearchParams(
							(prev) => {
								const next = new URLSearchParams(prev);
								next.set("company", first.id);
								return next;
							},
							{ replace: true },
						);
					}
				}
			} catch {
				// Error handled silently; pages will show empty state
			} finally {
				if (!cancelled) {
					setIsLoading(false);
					setIsInitialized(true);
				}
			}
		}

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [user?.id]);

	return {
		companies,
		selectedCompany,
		setSelectedCompany,
		contact,
		isLoading,
		isInitialized,
	};
}
